import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/lib/AuthContext";
import { AUTH } from "@/constants/testIds";
import { Sparkles, LogIn, Loader2 } from "lucide-react";

const LOGO_URL =
    "https://customer-assets-m6fa6gv7.emergentagent.net/job_b0f0f9b1-18c4-4169-9d57-74f35eb2af07/artifacts/z5a1xd9x_PLAD_GOLD_png.webp";

const DEMO_ACCOUNTS = [
    { role: "Admin", email: "admin@profecia.link", password: "Profecia@2026", testId: AUTH.demoAdminChip },
    { role: "Content Creator", email: "creator@profecia.link", password: "Creator@2026", testId: AUTH.demoCreatorChip },
];

export default function LoginPage() {
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState(null);
    const [busy, setBusy] = useState(false);
    const { login, user } = useAuth();
    const navigate = useNavigate();

    if (user) return <Navigate to="/" replace />;

    const submit = async (e) => {
        e?.preventDefault?.();
        setError(null);
        setBusy(true);
        const { ok, error: err } = await login(email.trim(), password);
        setBusy(false);
        if (ok) {
            // Role-based landing — creator to Tasks, admin to Forge
            navigate("/");
        } else {
            setError(err || "Login failed");
        }
    };

    const fillDemo = (a) => {
        setEmail(a.email);
        setPassword(a.password);
        setError(null);
    };

    return (
        <div className="min-h-screen grain-bg flex">
            {/* Left visual — brand + selling proposition */}
            <div className="hidden lg:flex flex-col justify-between w-1/2 p-14 relative overflow-hidden">
                <div className="flex items-center gap-4">
                    <img
                        src={LOGO_URL}
                        alt="Profecia Links"
                        className="h-12 w-auto object-contain"
                    />
                    <div className="gold-divider self-stretch" />
                    <div>
                        <div className="font-serif text-2xl leading-none text-[color:var(--text-main)]">PromptForge</div>
                        <div className="text-[10px] uppercase tracking-[0.3em] font-mono text-[color:var(--profecia-gold)] mt-1">
                            by Profecia Links
                        </div>
                    </div>
                </div>
                <div className="max-w-lg">
                    <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] font-mono text-[color:var(--profecia-gold)]">
                        <Sparkles size={12} /> Multi-Agent Prompt Refinement
                    </div>
                    <h1 className="font-serif text-5xl md:text-6xl leading-[1.05] text-[color:var(--text-main)] mt-4">
                        Turn a rough brief into a
                        <span className="italic text-[color:var(--profecia-gold)]"> production-ready </span>
                        Higgsfield prompt.
                    </h1>
                    <p className="text-[color:var(--text-muted)] font-light text-base leading-relaxed mt-6 max-w-md">
                        Six specialised agents — Analyst, Selector, Director,
                        Composer, Specialist, Assembler — orchestrated live in
                        under two minutes, with cost governance and prompt
                        engineering built in.
                    </p>
                </div>
                <div className="text-xs text-[color:var(--text-muted)] font-light">
                    Dubai · Dublin · Pune
                </div>
            </div>

            {/* Right form */}
            <div className="w-full lg:w-1/2 flex items-center justify-center px-6 py-14 bg-white/50 backdrop-blur-sm border-l border-[color:var(--border-subtle)]">
                <div className="w-full max-w-md">
                    <div className="lg:hidden flex items-center gap-3 mb-8">
                        <img src={LOGO_URL} alt="Profecia" className="h-9" />
                        <div className="font-serif text-xl">PromptForge</div>
                    </div>

                    <div className="text-[10px] uppercase tracking-[0.3em] font-mono text-[color:var(--profecia-gold)]">
                        Sign in
                    </div>
                    <h2 className="font-serif text-4xl text-[color:var(--text-main)] mt-1 leading-tight">
                        Welcome back
                    </h2>
                    <p className="text-sm text-[color:var(--text-muted)] font-light mt-2">
                        Sign in with your Profecia workspace credentials.
                    </p>

                    <form className="mt-8 space-y-4" onSubmit={submit}>
                        <label className="block">
                            <span className="text-[10px] uppercase tracking-widest font-mono text-[color:var(--text-muted)] mb-1.5 block">
                                Email
                            </span>
                            <input
                                data-testid={AUTH.loginEmail}
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                autoComplete="username"
                                required
                                className="w-full px-3.5 py-3 rounded-md border border-[color:var(--border-subtle)] bg-white text-[color:var(--text-main)] focus:outline-none focus:border-[color:var(--profecia-gold)] transition-colors"
                                placeholder="you@profecia.link"
                            />
                        </label>

                        <label className="block">
                            <span className="text-[10px] uppercase tracking-widest font-mono text-[color:var(--text-muted)] mb-1.5 block">
                                Password
                            </span>
                            <input
                                data-testid={AUTH.loginPassword}
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                autoComplete="current-password"
                                required
                                className="w-full px-3.5 py-3 rounded-md border border-[color:var(--border-subtle)] bg-white text-[color:var(--text-main)] focus:outline-none focus:border-[color:var(--profecia-gold)] transition-colors"
                            />
                        </label>

                        {error && (
                            <div
                                data-testid={AUTH.loginError}
                                className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2"
                            >
                                {error}
                            </div>
                        )}

                        <button
                            data-testid={AUTH.loginSubmit}
                            type="submit"
                            disabled={busy}
                            className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-md bg-[color:var(--profecia-gold)] text-white text-sm font-medium tracking-wide hover:bg-[color:var(--profecia-gold-hover)] disabled:opacity-50 transition-colors"
                        >
                            {busy ? (
                                <>
                                    <Loader2 size={14} className="animate-spin" /> Signing in…
                                </>
                            ) : (
                                <>
                                    <LogIn size={14} /> Sign in
                                </>
                            )}
                        </button>
                    </form>

                    <div className="mt-8 pt-6 border-t border-[color:var(--border-subtle)]">
                        <div className="text-[10px] uppercase tracking-widest font-mono text-[color:var(--text-muted)] mb-3">
                            Demo accounts — click to prefill
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                            {DEMO_ACCOUNTS.map((a) => (
                                <button
                                    key={a.email}
                                    type="button"
                                    data-testid={a.testId}
                                    onClick={() => fillDemo(a)}
                                    className="text-left px-3 py-2.5 rounded-md border border-[color:var(--border-subtle)] hover:border-[color:var(--profecia-gold-subtle)] bg-white hover:bg-[color:var(--surface-hover)] transition-colors"
                                >
                                    <div className="text-[10px] uppercase tracking-widest font-mono text-[color:var(--profecia-gold)]">
                                        {a.role}
                                    </div>
                                    <div className="text-xs text-[color:var(--text-main)] mt-1 truncate">
                                        {a.email}
                                    </div>
                                    <div className="text-[10px] font-mono text-[color:var(--text-muted)] mt-0.5">
                                        {a.password}
                                    </div>
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
